import React, { useMemo } from 'react';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';

import { BRAND_COLORS } from '~/components/brand/brandTokens';

import TableResizeHandle from './TableResizeHandle';
import { tableScrollbarSx } from './offerTableScrollSx';
import { Model, SelectionTuple, format, parseSelection, personFirst } from './presentationModel';
import useColumnOrder from './useColumnOrder';
import useScrollbarHeaderHeight from './useScrollbarHeaderHeight';
import useTableSizing from './useTableSizing';

type Target = Model['targetRows'][number];
/** On narrow screens the metric name stays pinned while the value columns scroll under it. */
const STICKY_METRIC_CLASS = 'kpi-sticky-metric';
type ValueKey = 'actual' | 'forecast' | 'target' | 'override';
const fixedTwo = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Workbook Targets sheets format Actual and Forecast (and MM / Agent Hub Target) as #,##0.00; others keep the default. */
function valueText(row: Target, id: ValueKey, view: string) {
  const value = row[id];
  const fixed = id === 'actual' || id === 'forecast' || (personFirst(view) && id === 'target');
  return fixed && typeof value === 'number' && Number.isFinite(value)
    ? fixedTwo.format(value)
    : format(value, row.unit);
}
type ColumnKey =
  | 'person'
  | 'region'
  | 'rsmRegion'
  | 'label'
  | 'onTrack'
  | 'actual'
  | 'forecast'
  | 'target'
  | 'override'
  | 'forecastRatio';
type Props = {
  rows: Model['targetRows'];
  /** Rows pinned under the table whatever the sort, e.g. the Agent Hub team total. Not clickable. */
  totalRows?: Model['teamTotalRows'];
  view: string;
  selection: string;
  sort: string;
  direction: 'asc' | 'desc';
  onSort: (key: string) => void;
  onSelect: (tuple: SelectionTuple, multi: boolean) => void;
};

export default function CommercialKpiTargets({
  rows,
  totalRows = [],
  view,
  selection,
  sort,
  direction,
  onSort,
  onSelect,
}: Props) {
  const scrollRef = useScrollbarHeaderHeight();
  const { tableRef, columnWidths, narrowedColumns, rowHeights, resizeAxis, startResize, resizeWithKeyboard } =
    useTableSizing();
  const selected = parseSelection(selection);
  const sorted = useMemo(() => {
    if (sort === 'source') {
      return rows;
    }
    const value = (row: Target) => {
      const field = row[sort as ColumnKey];
      return typeof field === 'number' || typeof field === 'string' ? field : null;
    };
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av === null) {
        return bv === null ? 0 : 1;
      }
      if (bv === null) {
        return -1;
      }
      const result = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return direction === 'asc' ? result : -result;
    });
  }, [rows, sort, direction]);
  const dimensions: Array<ColumnKey> = personFirst(view)
    ? ['person', 'region']
    : view === 'PCM'
      ? ['rsmRegion', 'person', 'region']
      : ['rsmRegion', 'region', 'person'];
  const columns: Array<{ id: ColumnKey; label: string }> = personFirst(view)
    ? [
        { id: 'person', label: 'PCM Name' },
        { id: 'region', label: 'PCM Region' },
      ]
    : [
        { id: 'rsmRegion', label: 'RSM Region' },
        ...(view === 'PCM'
          ? ([
              { id: 'person', label: 'PCM Name' },
              { id: 'region', label: 'PCM Region' },
            ] as const)
          : ([
              { id: 'region', label: 'PCM Region' },
              { id: 'person', label: 'PCM Name' },
            ] as const)),
      ];
  columns.push(
    { id: 'label', label: 'Metric Name' },
    { id: 'onTrack', label: 'On Track' },
    { id: 'actual', label: 'Actual' },
    { id: 'forecast', label: 'Forecast' },
    { id: 'target', label: 'Target' },
  );
  if (view === 'PCM' || view === 'Experience') {
    columns.push({ id: 'override', label: 'Override' });
  }
  columns.push({ id: 'forecastRatio', label: 'EOP P%' });
  const { order, columnDragProps } = useColumnOrder(columns.map(({ id }) => id));
  const visibleColumns = order.map((id) => columns.find((column) => column.id === id)).filter(Boolean);
  const fixedLayout = Object.keys(columnWidths).length > 0;
  function dimensionSpan(index: number, id: ColumnKey) {
    if (sort !== 'source' || !dimensions.includes(id)) {
      return 1;
    }
    const prefix = dimensions.slice(0, dimensions.indexOf(id) + 1);
    const same = (other: Target) => prefix.every((field) => other[field] === sorted[index][field]);
    if (index > 0 && same(sorted[index - 1])) {
      return 0;
    }
    let span = 1;
    while (index + span < sorted.length && same(sorted[index + span])) {
      span += 1;
    }
    return span;
  }
  // Native table rows can grow for wrapped content. Constrain content only when every row
  // covered by this cell has an explicit size, including merged owner/region cells.
  function contentStyle(rowIds: Array<string>, columnId: ColumnKey) {
    const height = rowIds.every((id) => rowHeights[id] !== undefined)
      ? rowIds.reduce((sum, id) => sum + rowHeights[id], 0) - 1
      : undefined;
    return {
      py: 0.75,
      px: 1,
      overflow: 'hidden',
      boxSizing: 'border-box',
      maxHeight: height,
      overflowWrap: narrowedColumns[columnId] ? 'break-word' : 'normal',
    };
  }
  return (
    // Keep the header visible within the table's scroll area on desktop and phones.
    <TableContainer
      ref={scrollRef}
      sx={{
        ...tableScrollbarSx,
        maxHeight: 480,
        overflow: 'auto',
        minHeight: 0,
        backgroundImage: (theme) =>
          `linear-gradient(to bottom, ${BRAND_COLORS.oceanTeal} 0 var(--scrollbar-header-height, 32px), ${theme.palette.background.paper} var(--scrollbar-header-height, 32px))`,
      }}
    >
      <Table
        ref={tableRef}
        stickyHeader
        size="small"
        sx={{
          // Grow/shrink the table itself, rather than taking width from neighbouring columns.
          ...(fixedLayout
            ? { tableLayout: 'fixed', width: columns.reduce((sum, { id }) => sum + columnWidths[id], 0), minWidth: 0 }
            : { minWidth: personFirst(view) ? 700 : 880 }),
          '& .MuiTableCell-root': {
            py: 0.75,
            px: 1,
            fontSize: 12,
            lineHeight: 1.45,
            overflowWrap: 'normal',
          },
          '& .MuiTableCell-body': { p: 0 },
          ...(resizeAxis ? { '&, & *': { cursor: resizeAxis === 'column' ? 'col-resize' : 'row-resize' } } : {}),
          [`& .MuiTableCell-body.${STICKY_METRIC_CLASS}`]: {
            position: { xs: 'sticky', md: 'relative' },
            left: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            boxShadow: { xs: 1, md: 'none' },
          },
          '& .MuiTableHead-root': { position: 'sticky', top: 0, zIndex: 5 },
          '& .MuiTableCell-head': {
            position: 'sticky',
            top: 0,
            zIndex: 5,
            bgcolor: BRAND_COLORS.oceanTeal,
            color: '#FFFFFF',
          },
          [`& .MuiTableCell-head.${STICKY_METRIC_CLASS}`]: { left: { xs: 0, md: 'auto' }, zIndex: 6 },
          '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-icon': {
            color: '#FFFFFF !important',
          },
        }}
        aria-label={`${view} KPI progress`}
      >
        {fixedLayout && (
          <Box component="colgroup">
            {visibleColumns.map(({ id }) => (
              <Box component="col" key={id} sx={{ width: columnWidths[id] }} />
            ))}
          </Box>
        )}
        <TableHead>
          <TableRow>
            {visibleColumns.map(({ id, label }) => (
              <TableCell
                key={id}
                {...columnDragProps(id)}
                data-column-id={id}
                sx={{ position: 'sticky', overflow: 'hidden', whiteSpace: narrowedColumns[id] ? 'normal' : 'nowrap' }}
                className={id === 'label' ? STICKY_METRIC_CLASS : undefined}
                align={['actual', 'forecast', 'target', 'override', 'forecastRatio'].includes(id) ? 'right' : 'left'}
              >
                <TableSortLabel
                  active={sort === id}
                  direction={sort === id ? direction : 'asc'}
                  onClick={() => onSort(id)}
                  sx={narrowedColumns[id] ? { maxWidth: '100%' } : undefined}
                >
                  {narrowedColumns[id] ? (
                    <Box component="span" sx={{ minWidth: 0, overflowWrap: 'break-word' }}>
                      {label}
                    </Box>
                  ) : (
                    label
                  )}
                </TableSortLabel>
                <TableResizeHandle
                  axis="column"
                  label={`Resize ${label} column`}
                  size={columnWidths[id]}
                  onPointerDown={(event) => startResize(event, 'column', id)}
                  onKeyDown={(event) => resizeWithKeyboard(event, 'column', id)}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, index) => {
            const active = selected.some(
              (tuple) =>
                tuple.person === row.person &&
                tuple.region === row.region &&
                (personFirst(view) || tuple.rsmRegion === row.rsmRegion),
            );
            return (
              <TableRow
                key={row.id}
                data-row-id={row.id}
                hover
                selected={active}
                onClick={(event) => onSelect(row.tuple, event.ctrlKey || event.metaKey || event.shiftKey)}
                sx={{ cursor: 'pointer', height: rowHeights[row.id] }}
              >
                {visibleColumns.map(({ id }) => {
                  const rowSpan = dimensionSpan(index, id);
                  if (!rowSpan) {
                    return null;
                  }
                  return (
                    <TableCell
                      key={id}
                      rowSpan={rowSpan}
                      className={id === 'label' ? STICKY_METRIC_CLASS : undefined}
                      sx={{
                        ...(dimensions.includes(id) ? { verticalAlign: 'top' } : {}),
                        ...(!dimensions.includes(id) ? { position: 'relative' } : {}),
                      }}
                      align={
                        ['actual', 'forecast', 'target', 'override', 'forecastRatio'].includes(id) ? 'right' : 'left'
                      }
                    >
                      <Box
                        sx={contentStyle(
                          sorted.slice(index, index + rowSpan).map(({ id }) => id),
                          id,
                        )}
                      >
                        {id === 'person' ? (
                          <Button
                            size="small"
                            sx={{
                              p: 0,
                              minWidth: 0,
                              fontSize: 'inherit',
                              lineHeight: 1.4,
                              textAlign: 'left',
                              ...(fixedLayout ? { display: 'block', maxWidth: '100%', whiteSpace: 'normal' } : {}),
                              overflowWrap: 'inherit',
                            }}
                          >
                            {row.person ?? 'Unknown'}
                          </Button>
                        ) : id === 'onTrack' ? (
                          <Tooltip
                            title={
                              row.target === 0
                                ? 'Recorded zero target. Workbook formula returns Yes and zero EOP P%.'
                                : 'Yes when forecast is at least target.'
                            }
                          >
                            <Box component="span">
                              {row.onTrack}
                              {row.target === 0 ? ' · zero target' : ''}
                            </Box>
                          </Tooltip>
                        ) : id === 'forecastRatio' ? (
                          format(row.forecastRatio, 'percent')
                        ) : ['actual', 'forecast', 'target', 'override'].includes(id) ? (
                          valueText(row, id as 'actual' | 'forecast' | 'target' | 'override', view)
                        ) : (
                          (row[id] ?? 'Unknown')
                        )}
                      </Box>
                      {!dimensions.includes(id) && (
                        <TableResizeHandle
                          axis="row"
                          keyboardAccessible={id === 'label'}
                          label={`Resize ${row.person ?? 'Unknown'} ${row.label} row`}
                          size={rowHeights[row.id]}
                          onPointerDown={(event) => startResize(event, 'row', row.id)}
                          onKeyDown={(event) => resizeWithKeyboard(event, 'row', row.id)}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          {totalRows.map((row, index) => (
            <TableRow key={row.id} data-row-id={row.id} sx={{ height: rowHeights[row.id] }}>
              {visibleColumns.map(({ id }) => (
                <TableCell
                  key={id}
                  align={['actual', 'forecast', 'target', 'override', 'forecastRatio'].includes(id) ? 'right' : 'left'}
                  sx={{
                    position: 'relative',
                    fontWeight: 600,
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#0F0C08' : '#F3F1EA'),
                    ...(index === 0 ? { borderTop: 2, borderTopColor: 'divider' } : {}),
                  }}
                >
                  <Box sx={contentStyle([row.id], id)}>
                    {id === 'forecastRatio'
                      ? format(row.forecastRatio, 'percent')
                      : ['actual', 'forecast', 'target', 'override'].includes(id)
                        ? valueText(row, id as ValueKey, view)
                        : id === 'onTrack'
                          ? row.onTrack
                          : (row[id] ?? '')}
                  </Box>
                  {!dimensions.includes(id) && (
                    <TableResizeHandle
                      axis="row"
                      keyboardAccessible={id === 'label'}
                      label={`Resize team ${row.label} row`}
                      size={rowHeights[row.id]}
                      onPointerDown={(event) => startResize(event, 'row', row.id)}
                      onKeyDown={(event) => resizeWithKeyboard(event, 'row', row.id)}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!sorted.length && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                No source rows match the global filters. Values are Unknown.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
